import { useContext, useEffect, useState } from 'react';
import IdentityContext from '../../../contexts/IdentityContext';
import ModalContext from '../../../contexts/ModalContext';
import useRouting from '../../../hooks/useRouting';
import useThrowAsyncError from '../../../hooks/useThrowAsyncError';
import { useTranslation } from 'react-i18next';
import {
  fetchPrivacyPolicy,
  fetchTermsAndConditions,
  PrivacyPolicyResponse,
  TermsAndConditionsResponse,
} from '../api/TermsOfUseApi';
import TermsOfUseModal from '../TermsOfUseModal';

export enum DescriptionKey {
  INITIAL = 'INITIAL',
  TERMS_AND_CONDITIONS = 'TERMS_AND_CONDITIONS',
  PRIVACY_POLICY = 'PRIVACY_POLICY',
  BOTH = 'BOTH',
}

const getDescriptionKey = (
  termsAndConditions: TermsAndConditionsResponse,
  privacyPolicy: PrivacyPolicyResponse,
): DescriptionKey => {
  if (termsAndConditions.neverAccepted && privacyPolicy.neverAccepted) {
    return DescriptionKey.INITIAL;
  } else if (termsAndConditions.accepted && !privacyPolicy.accepted) {
    return DescriptionKey.PRIVACY_POLICY;
  } else if (!termsAndConditions.accepted && privacyPolicy.accepted) {
    return DescriptionKey.TERMS_AND_CONDITIONS;
  }

  return DescriptionKey.BOTH;
};

const useTermsOfUse = () => {
  const { REACT_APP_P_STUDIO_GROUP } = process.env;
  const { t } = useTranslation();
  const { r } = useRouting();
  const handleError = useThrowAsyncError();

  const { identity } = useContext(IdentityContext);
  const { showModal } = useContext(ModalContext);

  const [verifyTermsOfUse, setVerifyTermsOfUse] = useState(identity.isAuthenticated);

  useEffect(() => {
    if (identity.isAuthenticated) setVerifyTermsOfUse(true);
  }, [identity.isAuthenticated]);

  useEffect(() => {
    const listener = () => {
      setVerifyTermsOfUse(true);
    };

    window.addEventListener('tokens-updated', listener);

    return () => window.removeEventListener('tokens-updated', listener);
  }, []);

  useEffect(() => {
    const getAcceptedTermsOfUse = async () => {
      if (!verifyTermsOfUse) return;
      if (window.location.pathname === r('privacy-policy')) return;
      if (window.location.pathname === r('terms-and-conditions')) return;

      const [termsAndConditions, privacyPolicy] = await Promise.all([
        fetchTermsAndConditions(REACT_APP_P_STUDIO_GROUP),
        fetchPrivacyPolicy(REACT_APP_P_STUDIO_GROUP),
      ]);

      const description = t(
        `SERVICES.TERMS_OF_USE_DIALOG.DESCRIPTION.${getDescriptionKey(termsAndConditions, privacyPolicy)}`,
      );

      if (!termsAndConditions.accepted || !privacyPolicy.accepted) {
        showModal(<TermsOfUseModal id="terms-of-use-modal" description={description} />, true);
      }

      setVerifyTermsOfUse(false);
    };

    getAcceptedTermsOfUse().catch(handleError);
  }, [REACT_APP_P_STUDIO_GROUP, r, showModal, t, handleError, verifyTermsOfUse]);

  return null;
};

export default useTermsOfUse;
